import React, { useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Bitcoin, AlertCircle } from 'lucide-react';
import { PaymentMethod } from '@/services/platformApi';

interface BitcoinWalletFormProps {
  onSubmit: (method: Omit<PaymentMethod, 'id' | 'createdAt'>) => Promise<void>;
  loading: boolean;
  onCancel: () => void;
}

const BitcoinWalletForm: React.FC<BitcoinWalletFormProps> = ({
  onSubmit,
  loading,
  onCancel
}) => {
  const [address, setAddress] = useState('');
  const [wallet, setWallet] = useState('');
  const [network, setNetwork] = useState('mainnet');
  const [error, setError] = useState('');

  const validateBitcoinAddress = (address: string, network: string): boolean => {
    if (!address) return false;
    
    // Basic Bitcoin address validation
    const patterns = {
      mainnet: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/,
      testnet: /^[2mn][a-km-zA-HJ-NP-Z1-9]{25,34}$|^tb1[a-z0-9]{39,59}$/
    };
    
    return patterns[network as keyof typeof patterns]?.test(address) || false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!address.trim()) {
      setError('Bitcoin address is required');
      return;
    }

    if (!validateBitcoinAddress(address, network)) {
      setError('Invalid Bitcoin address format');
      return;
    }

    if (!wallet.trim()) {
      setError('Wallet name is required');
      return;
    }

    try {
      await onSubmit({
        type: 'bitcoin',
        isDefault: false,
        details: {
          address: address.trim(),
          wallet: wallet.trim(),
          network: network,
          // Show only first 6 and last 4 characters for security
          last4: `${address.slice(0, 6)}...${address.slice(-4)}`,
          brand: 'Bitcoin',
        },
        active: true,
      });
    } catch (error) {
      setError('Failed to add Bitcoin wallet. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center py-2">
        <Bitcoin className="w-12 h-12 mx-auto text-orange-500 mb-2" />
        <h3 className="font-medium">Bitcoin Wallet</h3>
        <p className="text-sm text-muted-foreground">
          Connect your Bitcoin wallet for payments
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div>
          <Label htmlFor="wallet">Wallet Name</Label>
          <Input
            id="wallet"
            placeholder="My Bitcoin Wallet"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            disabled={loading}
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            A friendly name for your wallet
          </p>
        </div>

        <div>
          <Label htmlFor="network">Network</Label>
          <Select value={network} onValueChange={setNetwork} disabled={loading}>
            <SelectTrigger>
              <SelectValue placeholder="Select network" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mainnet">Bitcoin Mainnet</SelectItem>
              <SelectItem value="testnet">Bitcoin Testnet</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Select the Bitcoin network for your wallet
          </p>
        </div>

        <div>
          <Label htmlFor="address">Bitcoin Address</Label>
          <Input
            id="address"
            placeholder="bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={loading}
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Your Bitcoin wallet address for receiving payments
          </p>
        </div>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="font-medium text-orange-800 mb-2">Important Notes:</h4>
        <ul className="text-sm text-orange-700 space-y-1">
          <li>• Only use addresses from wallets you control</li>
          <li>• Double-check the address before submitting</li>
          <li>• Bitcoin transactions are irreversible</li>
          <li>• Consider network fees when making transactions</li>
        </ul>
      </div>

      <div className="flex space-x-2">
        <Button type="submit" className="flex-1" disabled={loading}>
          {loading ? 'Adding Wallet...' : 'Add Bitcoin Wallet'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default BitcoinWalletForm; 